'use client';
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import { useEffect, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper/types";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { LanguageType, LanguageDirection, AboutImageSectionContent } from "@/lib/types";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from 'gsap';
import { useGSAP } from "@gsap/react";
import Image from 'next/image';

import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
gsap.registerPlugin(ScrollTrigger, useGSAP);

interface ImageCard {
  id: number;
  imageUrl: string;
  imageAltText: string;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  "english": "ltr",
  "arabic": "rtl"
};

// const imageCards: ImageCard[] = [
//   {
//     id: 1,
//     // imageUrl: "https://picsum.photos/1200/800?random=1",
//     imageUrl: "./hero-image.avif",
//     title: "Card 1",
//     description: "Description for card 1",
//   },
//   {
//     id: 2,
//     // imageUrl: "https://picsum.photos/1200/800?random=2",
//     imageUrl: "./hero-image.avif",
//     title: "Card 2",
//     description: "Description for card 2",
//   },
//   {
//     id: 3,
//     // imageUrl: "https://picsum.photos/1200/800?random=3",
//     imageUrl: "./hero-image.avif",
//     title: "Card 3",
//     description: "Description for card 3",
//   },
//   {
//     id: 4,
//     // imageUrl: "https://picsum.photos/1200/800?random=4",
//     imageUrl: "./hero-image.avif",
//     title: "Card 4",
//     description: "Description for card 3",
//   },
//   {
//     id: 5,
//     // imageUrl: "https://picsum.photos/1200/800?random=5",
//     imageUrl: "./hero-image.avif",
//     title: "Card 5",
//     description: "Description for card 3",
//   },
//   {
//     id: 6,
//     // imageUrl: "https://picsum.photos/1200/800?random=6",
//     imageUrl: "./hero-image.avif",
//     title: "Card 6",
//     description: "Description for card 3",
//   },

// ];

interface ImageSwiperSectionProps {
  language?: LanguageType;
  onInitialPosition?: (position: number) => void;
  aboutImageSectionPageData?: AboutImageSectionContent;
}

const ImageSwiperSection: React.FC<ImageSwiperSectionProps> = ({
  language = "english",
  onInitialPosition,
  aboutImageSectionPageData
}) => {
  const isLg = useIsLg();
  const swiperRef = useRef<SwiperType | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState('1264px');
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const imageCards: ImageCard[] = aboutImageSectionPageData?.images.map((image: any, index: number) => ({
    id: index,
    imageUrl: image.carouselImage.url,
    imageAltText: image.carouselImageAltText
  })) ?? [];

  // debug.log('rendering image swiper section');

  // Calculate card width on mount and when width changes
  useEffect(() => {
    const updateCardWidth = () => {
      const containerWidth = containerRef.current?.offsetWidth ?? 1605;
      setCardWidth(isLg ? `${containerWidth / 2.5}px` : `${containerWidth / 1.2}px`);
    };

    updateCardWidth();

    let lastWidth = window.innerWidth;
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      // Skip height-only changes (mobile browser chrome appearing/disappearing)
      const currentWidth = window.innerWidth;
      if (currentWidth === lastWidth) return;
      lastWidth = currentWidth;

      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(updateCardWidth, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [isLg]);

  const handleNext = () => {
    if (swiperRef.current) {
      if (allowSlideNext()) {
        swiperRef.current.slideTo(swiperRef.current.activeIndex + 1, 300, true);
      }
    }
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      const prevIndex = swiperRef.current.activeIndex - 1;
      swiperRef.current.slideTo(prevIndex, 300, true);
    }
  };

  useEffect(() => {
    // ScrollTrigger.refresh();

    const updateProgress = () => {
      if (swiperRef.current && progressBarRef.current) {
        const progress = swiperRef.current.progress * 100;
        progressBarRef.current.style.width = `${progress}%`;
      }
    };

    if (swiperRef.current) {
      swiperRef.current.on("progress", updateProgress);


      // swiperRef.current.on('update', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('slidesUpdated', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('snapGridLengthChange', () => {
      //   ScrollTrigger.refresh();
      // });

      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];

      updateProgress();
    }


    return () => {
      if (swiperRef.current) {
        // swiperRef.current.off('update');
        // swiperRef.current.off('slidesUpdated');
        // swiperRef.current.off('snapGridLengthChange');
        swiperRef.current.off('progress', updateProgress);
        // ScrollTrigger.refresh();
      }
    };
  }, []);

  useEffect(() => {
    if (containerRef.current && onInitialPosition) {
      const rect = containerRef.current.getBoundingClientRect();
      const fortyPercentY = window.scrollY + rect.top + (rect.height * 0.4);
      if (!isLg) {
        ScrollTrigger.refresh();
      }
      onInitialPosition(fortyPercentY);
    }
  }, [cardWidth, onInitialPosition, isLg]);

  // useEffect(() => {
  //   ScrollTrigger.refresh();
  // }, [isLg]);

  const allowSlideNext = () => {

    const result = (swiperRef
      && swiperRef.current
      && (swiperRef.current?.realIndex < imageCards.length - 1)
      || false);
    return result;
  }

  useGSAP(() => {
    const container = containerRef.current;
    if (container && isLg) {
      const cards: HTMLElement[] = Array.from(document.querySelectorAll('.swiper-image-card'));

      gsap.fromTo(cards, {
        yPercent: -10
      }, {
        yPercent: 10,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: container,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          // markers: true,
          // id: "image-swiper-section-scroll-trigger",
          onEnter: () => {
            cards.forEach(card => {
              card.style.willChange = "transform";
            });
          },
          onLeave: () => {
            cards.forEach(card => {
              card.style.willChange = "auto";
            });
          },
          onEnterBack: () => {
            cards.forEach(card => {
              card.style.willChange = "transform";
            });
          },
          onLeaveBack: () => {
            cards.forEach(card => {
              card.style.willChange = "auto";
            });
          }
        }
      });
    }
  }, { dependencies: [isLg], revertOnUpdate: true });


  return (

    // section has custom padding!
    <section
      style={{ overscrollBehaviorX: 'none' }}
      // pt-0
      // padding-main-t-desktop
      className="
      w-full
      padding-main-x-mobile
      padding-main-x-desktop
      flex items-center justify-center
      overflow-hidden
      relative
    ">
      <div className="w-full container-x-large overflow-visible " ref={containerRef}>
        <div className="w-full flex flex-col gap-[2rem] lg:gap-[3.5rem] overflow-visible items-center justify-center">
          <Swiper
            modules={[Mousewheel]}
            style={{
              direction: LANGUAGE_TO_DIRECTION[language],
              width: '100%',
              overflow: 'visible'
            }}
            className="
              overflow-visible
              "
            spaceBetween={isLg ? 16 : 12}
            slidesPerView="auto"
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            onAfterInit={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            centeredSlidesBounds={false}
            centeredSlides={false}
            // mousewheel={{
            //   forceToAxis: true,
            //   sensitivity: 0.1,
            //   releaseOnEdges: true,
            // }}
          >

            {imageCards.map((card) => (
              <SwiperSlide key={card.id} style={{ width: 'auto' }} className="overflow-visible cursor-grab">
                <div
                  className="
                  relative w-full
                  overflow-hidden
                  border-radius-main border-secondary border-stroke-soft-200"
                >
                  <Image
                    src={card.imageUrl}
                    alt={card.imageAltText}
                    width={600}
                    height={700}
                    sizes="(min-width: 992px) 40vw, 80vw"
                    loading="eager"
                    className="swiper-image-card"
                    style={{
                      scale: 1.1,
                      width: cardWidth,
                      height: 'auto',
                      aspectRatio: '6/7',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              </SwiperSlide>
            ))}
            {/* <SwiperSlide style={{ width: 'auto' }}>
                <div className="invisible border w-[0.5rem] lg:w-[2.5rem]"></div>
              </SwiperSlide> */}


          </Swiper >
          <div
            style={{
              direction: LANGUAGE_TO_DIRECTION[language],
            }}
            className="w-full container-medium flex flex-col gap-[1.5rem] lg:gap-[2rem] px-[0.5rem] lg:px-0">
            {isLg && <div className="
              w-full
              flex justify-end
            ">
              <div style={{ direction: "ltr" }} className="w-fit flex flex-row  gap-[0.75rem] lg:gap-[1rem] ">
                <CompactButton
                  size={isLg ? 64 : 44}
                  radius={9999}
                  className="left-0 bottom-0"
                  disabled={language === "english" ? isBeginning : isEnd}
                  onClick={() => {
                    if (language === "english") {
                      handlePrev();
                    } else {
                      handleNext();
                    }
                  }}
                >
                  <CaretLeft />
                </CompactButton>

                <CompactButton
                  size={isLg ? 64 : 44}
                  radius={9999}
                  className="right-0 bottom-0"
                  disabled={language === "english" ? isEnd : isBeginning}
                  onClick={() => {
                    if (language === "english") {
                      handleNext();
                    } else {
                      handlePrev();
                    }
                  }}
                >
                  <CaretRight />
                </CompactButton>
              </div>
            </div>}

            <div
              className="w-full bg-stroke-soft-200 h-[1.5px]">
              <div
                ref={progressBarRef}
                className="bg-theme-yellow-primary-base h-[2px] transition-all duration-300"
              ></div>
            </div >
          </div >

        </div >
      </div >
    </section >

  );
};

export default ImageSwiperSection;
